import React from 'react'
import { CheckCircle, XCircle, AlertCircle } from 'lucide-react'

interface AnalysisResultViewProps {
  analysis: any
}

export const AnalysisResultView: React.FC<AnalysisResultViewProps> = ({ analysis }) => {
  if (!analysis) return <div className="text-gray-400">No analysis result.</div>

  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h4 className="text-xl font-medium text-gray-900 mb-4">Recommended Option</h4>
        <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 mb-4">
          <p className="text-primary-900 font-medium">{analysis.recommendedOption}</p>
        </div>
        <div className="flex items-center space-x-2 mb-4">
          <span className="text-sm text-gray-600">Confidence Level:</span>
          <div className="flex-1 bg-gray-200 rounded-full h-2">
            <div 
              className="bg-primary-600 h-2 rounded-full" 
              style={{ width: `${analysis.confidence}%` }}
            ></div>
          </div>
          <span className="text-sm font-medium text-gray-900">{analysis.confidence}%</span>
        </div>
        <p className="text-gray-700">{analysis.reasoning}</p>
      </div>

      <div className="space-y-4">
        <h4 className="text-lg font-medium text-gray-900">Detailed Analysis</h4>
        {analysis.detailedAnalysis && analysis.detailedAnalysis.map((item: any, index: number) => (
          <div key={index} className="border border-gray-200 rounded-lg p-4">
            <h5 className="font-medium text-gray-900 mb-2">{item.option}</h5>
            <div className="flex items-center space-x-2 mb-2">
              <span className="text-sm text-gray-600">Score:</span>
              <span className="text-sm font-medium text-gray-900">{item.score}/100</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h6 className="text-sm font-medium text-green-700 mb-1">Strengths</h6>
                <ul className="text-sm text-gray-600 space-y-1">
                  {item.strengths && item.strengths.map((strength: string, i: number) => (
                    <li key={i} className="flex items-center space-x-1">
                      <CheckCircle className="w-3 h-3 text-green-600" />
                      <span>{strength}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <h6 className="text-sm font-medium text-red-700 mb-1">Weaknesses</h6>
                <ul className="text-sm text-gray-600 space-y-1">
                  {item.weaknesses && item.weaknesses.map((weakness: string, i: number) => (
                    <li key={i} className="flex items-center space-x-1">
                      <XCircle className="w-3 h-3 text-red-600" />
                      <span>{weakness}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Risk Assessment */}
      {analysis.riskAssessment && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h4 className="text-lg font-medium text-yellow-900 mb-3">Risk Assessment</h4>
          <div className="space-y-3">
            <div className="flex items-center space-x-2">
              <span className="text-sm font-medium text-yellow-800">Overall Risk:</span>
              <span className={`px-2 py-1 rounded text-xs font-medium ${
                analysis.riskAssessment.overallRisk === 'low' ? 'bg-green-100 text-green-800' :
                analysis.riskAssessment.overallRisk === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                'bg-red-100 text-red-800'
              }`}>
                {analysis.riskAssessment.overallRisk?.toUpperCase()}
              </span>
            </div>
            {analysis.riskAssessment.risks && (
              <div>
                <h5 className="text-sm font-medium text-yellow-800 mb-1">Key Risks:</h5>
                <ul className="text-sm text-yellow-700 space-y-1">
                  {analysis.riskAssessment.risks.map((risk: string, i: number) => (
                    <li key={i} className="flex items-center space-x-1">
                      <AlertCircle className="w-3 h-3" />
                      <span>{risk}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {analysis.riskAssessment.mitigation && (
              <div>
                <h5 className="text-sm font-medium text-yellow-800 mb-1">Mitigation Strategies:</h5>
                <ul className="text-sm text-yellow-700 space-y-1">
                  {analysis.riskAssessment.mitigation.map((strategy: string, i: number) => (
                    <li key={i} className="flex items-center space-x-1">
                      <CheckCircle className="w-3 h-3" />
                      <span>{strategy}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Next Steps */}
      {analysis.nextSteps && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <h4 className="text-lg font-medium text-blue-900 mb-3">Recommended Next Steps</h4>
          <div className="space-y-2">
            {analysis.nextSteps.map((step: string, i: number) => (
              <div key={i} className="flex items-start space-x-2">
                <span className="w-6 h-6 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">
                  {i + 1}
                </span>
                <span className="text-blue-800">{step}</span>
              </div>
            ))}
          </div>
          {analysis.timeline && (
            <div className="mt-3 pt-3 border-t border-blue-200">
              <span className="text-sm font-medium text-blue-800">Timeline: </span>
              <span className="text-sm text-blue-700">{analysis.timeline}</span>
            </div>
          )}
        </div>
      )}
    </div>
  )
} 